import { useRef, useState } from 'react';
import type { KnownIssue } from '../api/useKnownIssues.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { Ikon } from '../status/Ikon.tsx';

/** DD-31 ordrett. Varselteksten er leietakerens; plattformen sender bare versjonen i varselet. */
export function varseltekst(versjon: string): string {
  return `Rettet i versjon ${versjon || '…'}. Ser du det fortsatt etter oppdatering, send et nytt innspill.`;
}

/**
 * «Rettet»-dialogen: viser antallet som varsles og varselteksten FØR bekreftelse (DD-31).
 * Bekreftelsesdialog, ikke angre-toast: handlingen er ikke angrbar (fikset → aktiv finnes
 * ikke) og rammer alle som har spurt (ux-flow-architect). Tallene kommer fra GET, hentet først.
 */
export function RettetDialog({ feil, opptatt, onBekreft, onAvbryt }: { feil: KnownIssue; opptatt: boolean; onBekreft: (fixedIn: string) => void; onAvbryt: () => void }) {
  const [versjon, setVersjon] = useState(feil.fixedIn ?? '');
  const felt = useRef<HTMLInputElement | null>(null);
  const v = versjon.trim();

  const grunn = !v ? 'Versjon mangler — varselet bærer den' : opptatt ? 'Sender …' : null;

  return (
    <Dialog tittelId="rettet-h" onLukk={onAvbryt} testid="rettet-dialog" fokusFørst={felt}>
      <div
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.ctrlKey && !grunn) {
            e.preventDefault();
            onBekreft(v);
          }
        }}
      >
        <h2 id="rettet-h" className="panel-h2">
          <Ikon navn="checkmark-circle-outline" /> Merk som rettet
        </h2>
        <p className="panel-tekst">«{feil.title}»</p>
        <label className="panel-felt">
          <span>
            Rettet i versjon <span className="panel-etikett">(obligatorisk)</span>
          </span>
          <input ref={felt} type="text" value={versjon} onChange={(e) => setVersjon(e.target.value)} placeholder="2.4.0" maxLength={32} aria-required="true" aria-invalid={!v || undefined} data-testid="rettet-versjon" />
          <span className="panel-etikett">Forhåndsutfylt fra «Rettes i versjon». Bærer varselet og styrer hvem kortet skjules for.</span>
        </label>
        <div role="note" aria-label="Varselteksten" className="panel-forhaandsvisning" data-testid="rettet-varsel">
          <p className="panel-etikett">
            <Ikon navn="mail-outline" /> Varselet — ordrett, til alle {feil.reach} som har spurt:
          </p>
          <p>«{varseltekst(v)}»</p>
        </div>
        <p className="panel-tekst-liten" data-testid="rettet-tall">
          <strong>{feil.reach} varsles</strong> personlig gjennom systemet deres: {feil.confirmations} sa «ja, jeg også» og {feil.related} svarte «ligner, men ikke helt» — begge står i varselet, hver for seg.
          {feil.anonymousCount ? ` ${feil.anonymousCount} anonyme kan ikke varsles.` : ''} Sakene som sa «ja, jeg også» går til Rettet. Kortet vises fortsatt for dem som har versjon {v || '…'} eller eldre, og skjules for nyere. Den gamle saken gjenåpnes aldri.
        </p>
        <div className="panel-handlinger">
          <Knapp label={`Rettet — varsle ${feil.reach}`} kbd="Ctrl+Enter" primaer grunn={grunn} onClick={() => onBekreft(v)} testid="rettet-bekreft" />
          <Knapp label="Avbryt" kbd="Esc" onClick={onAvbryt} testid="rettet-avbryt" />
        </div>
      </div>
    </Dialog>
  );
}
